import * as THREE from 'three';
import { DEEP_CRYPT } from '@somnhold/content';
import { hexDist, hexToWorld, type DungeonLayout, type DungeonPuzzle, type Hex } from '@somnhold/sim';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { WORLD_PER_HEX } from './coords.ts';
import type { GlowLayer } from './glow.ts';
import { SealPlates } from './seal.ts';
import type { Vfx } from './vfx.ts';

/*
 * The deep crypt's puzzles (docs/NIVELES.md), each readable on the floor without words:
 *
 * - Braziers: four iron bowls on tripods. Unlit, cold grey ash; lit, a flame whose size and the arc of
 *   the ring at its foot show the time left (a dial of ten ticks, one a second); the bowl being lit fills
 *   the ring in white. All four burning: a great flare.
 * - Levers: pressure plates in pairs, a lever beside each. Only the round's pair glows (red, pulsing);
 *   a plate with a hero on it goes white and its lever drops; a line of light between the two fills from
 *   both ends as they hold. Finished rounds stay dimly lit.
 * - Runes: the embers seal, with four plates.
 * - The glass bridge: the room's floor is thin glass over a void (each tile looks the same); the safe
 *   path glows for a moment, a pulse running from the door in to the door out, and the tiles anyone has
 *   reached stay faintly lit. A fall shatters the tile for a few seconds.
 *
 * Each puzzle draws in two or three calls (merged decals, instanced props); the other floors' are culled.
 */

const K = WORLD_PER_HEX / 1000;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

const at = (h: Hex, ground: (x: number, z: number) => number, lift = 0): THREE.Vector3 => {
  const c = hexToWorld(h.q, h.r);
  const x = c.x * K, z = c.y * K;
  return new THREE.Vector3(x, ground(x, z) + lift, z);
};

const VERT = /* glsl */ `
attribute vec2 aLocal;
attribute vec4 aState;
varying vec2 vL;
varying vec4 vS;
void main() {
  vL = aLocal;
  vS = aState;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

/** The ring at a brazier's foot: x time left (0 → 1), y lighting (0 → 1), z the flare when all burn. */
const BRAZIER_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vL;
varying vec4 vS;
void main() {
  float d = length(vL);
  float ang = atan(vL.x, vL.y) / 6.2831 + 0.5;
  float band = smoothstep(0.8, 0.83, d) * (1.0 - smoothstep(0.93, 0.96, d));
  float burn = vS.x;
  float hold = vS.y;
  float lit = step(ang, burn) * step(0.001, burn);
  float hl = step(ang, hold) * (1.0 - lit);
  float low = 1.0 - smoothstep(0.15, 0.3, burn);
  float blink = mix(1.0, 0.55 + 0.45 * step(0.5, fract(uTime * 3.0)), low * step(0.001, burn));
  vec3 fire = mix(vec3(1.0, 0.16, 0.05), vec3(1.0, 0.4, 0.1), burn);
  float tick = step(0.9, fract(ang * 10.0)) * band;
  vec3 col = band * (fire * lit * 0.95 * blink + vec3(0.92, 0.94, 1.0) * hl * 1.2 + vec3(0.13, 0.12, 0.13) * (1.0 - lit) * (1.0 - hl));
  col += vec3(0.02) * tick;
  float scorch = (1.0 - smoothstep(0.0, 0.5, d)) * 0.55;
  col += fire * (1.0 - smoothstep(0.0, 0.75, d)) * 0.18 * burn;
  col += vec3(1.0, 0.7, 0.4) * vS.z * (1.0 - smoothstep(0.0, 1.0, d)) * 0.6;
  float a = max(band * (0.55 + 0.45 * max(lit, hl)), scorch);
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/**
 * A lever's plate: x active (this round), y someone on it, z round done, w how long they have held (0 → 1). The
 * shader never names a variable `active`: it is a reserved word in GLSL ES 3.00 and the program fails to compile.
 */
const LEVER_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vL;
varying vec4 vS;
void main() {
  vec2 a = abs(vL);
  float box = max(a.x, a.y);
  float inside = 1.0 - smoothstep(0.96, 1.0, box);
  float rim = smoothstep(0.7, 0.75, box) * (1.0 - smoothstep(0.9, 0.95, box));
  float inner = smoothstep(0.42, 0.46, box) * (1.0 - smoothstep(0.5, 0.54, box));
  float slot = (1.0 - smoothstep(0.07, 0.1, a.x)) * (1.0 - smoothstep(0.38, 0.41, a.y));
  float lit = vS.x;
  float on = vS.y;
  float done = vS.z;
  float ang = atan(vL.x, vL.y) / 6.2831 + 0.5;
  float fill = step(ang, vS.w) * lit;
  float pulse = 0.6 + 0.4 * sin(uTime * 4.0);
  vec3 iron = mix(vec3(0.13, 0.13, 0.15), vec3(0.06, 0.06, 0.07), smoothstep(0.88, 0.96, box));
  vec3 red = vec3(1.0, 0.31, 0.23);
  vec3 white = vec3(0.95, 0.95, 1.0);
  vec3 glow = mix(red * pulse, white, on) * lit + vec3(0.5, 0.2, 0.15) * done * (1.0 - lit);
  vec3 col = iron;
  col = mix(col, vec3(0.012), slot);
  col += glow * (rim * 2.0 + inner * 0.8 + slot * 1.0);
  col += white * fill * rim * 1.4;
  col += mix(red, white, on) * lit * (1.0 - smoothstep(0.0, 0.9, box)) * 0.12 * (0.5 + on);
  float dim = 1.0 - max(lit, done);
  col += vec3(0.22, 0.21, 0.24) * rim * dim;
  gl_FragColor = vec4(col, inside);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/** A tile of the glass bridge: x the path showing (0 → 1), y reached, z shattered (1 just broken → 0), w a seed. */
const BRIDGE_FRAG = /* glsl */ `
uniform float uTime;
varying vec2 vL;
varying vec4 vS;
float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233)) + vS.w * 17.0) * 43758.5453); }
void main() {
  vec2 a = abs(vL);
  // Pointy side toward ±z (local y): flat edges at |x| = 1.
  float hx = max(a.x, a.x * 0.5 + a.y * 0.866);
  float inside = 1.0 - smoothstep(0.985, 1.0, hx);
  float edge = smoothstep(0.9, 0.975, hx) * inside;
  // The void under the glass: almost black, a violet haze, a few cold motes drifting far below.
  vec2 g = vL * 5.0 + vec2(uTime * 0.03, uTime * 0.02) + vS.ww * 7.0;
  float star = step(0.93, hash(floor(g))) * (1.0 - smoothstep(0.05, 0.2, length(fract(g) - 0.5)));
  vec3 voidC = vec3(0.006, 0.004, 0.012) + vec3(0.025, 0.01, 0.05) * (1.0 - hx) + vec3(0.5, 0.45, 0.8) * star * 0.3;
  // The glass: a streak of sheen, a pale bevel on every tile.
  float sheen = exp(-pow((vL.x * 0.7 + vL.y * 0.7 - 0.25 + sin(uTime * 0.4 + vS.w * 6.0) * 0.3) / 0.25, 2.0)) * 0.05;
  vec3 col = voidC + vec3(0.7, 0.72, 0.9) * sheen + vec3(0.32, 0.3, 0.42) * edge * 0.6;
  float path = vS.x;
  float got = vS.y;
  vec3 lightC = vec3(0.78, 0.82, 1.0);
  col += lightC * path * (0.45 + 0.55 * (1.0 - hx)) + lightC * edge * path * 1.4;
  col += vec3(0.42, 0.36, 0.72) * got * (0.18 + 0.4 * edge);
  // Shattered: a jagged hole, the void bare.
  float br = vS.z;
  float ang = atan(vL.y, vL.x);
  float seg = ang / 6.2831 * 9.0;
  float e = mix(0.55 + 0.25 * hash(vec2(floor(seg), 1.0)), 0.55 + 0.25 * hash(vec2(floor(seg) + 1.0, 1.0)), fract(seg));
  float hole = (1.0 - smoothstep(e - 0.02, e, length(vL))) * step(0.01, br);
  float crk = (1.0 - smoothstep(0.0, 0.025, abs(sin(ang * 3.5 + vS.w * 9.0)) * length(vL))) * step(0.01, br) * (1.0 - hole);
  col = mix(col, vec3(0.0, 0.0, 0.006), hole) + vec3(0.9, 0.88, 1.0) * crk * 0.6 * br;
  gl_FragColor = vec4(col, inside * 0.95);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

const decal = (frag: string, uniforms: Record<string, THREE.IUniform>): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERT,
    fragmentShader: frag,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -8,
  });

/** Flat quads (local -1..1) at each centre, merged; returns the mesh and its per-vertex state. */
const quads = (centers: THREE.Vector3[], half: number, mat: THREE.Material): { mesh: THREE.Mesh; state: THREE.BufferAttribute } => {
  const pos: number[] = [];
  const local: number[] = [];
  const index: number[] = [];
  centers.forEach((c, n) => {
    for (const [lx, lz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      pos.push(c.x + lx * half, c.y, c.z + lz * half);
      local.push(lx, -lz);
    }
    const b = n * 4;
    index.push(b, b + 2, b + 1, b, b + 3, b + 2);
  });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('aLocal', new THREE.Float32BufferAttribute(local, 2));
  const state = new THREE.Float32BufferAttribute(new Float32Array(centers.length * 16), 4).setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aState', state);
  geo.setIndex(index);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  mesh.renderOrder = 1;
  return { mesh, state };
};

/** Hexagonal tiles (pointy side toward ±z, like the board's) of apothem `size`, merged. */
const hexes = (centers: THREE.Vector3[], size: number, mat: THREE.Material): { mesh: THREE.Mesh; state: THREE.BufferAttribute; per: number } => {
  const RINGS = 2;
  const per = 1 + RINGS * 6;
  const pos: number[] = [];
  const local: number[] = [];
  const index: number[] = [];
  centers.forEach((c, n) => {
    const base = n * per;
    pos.push(c.x, c.y, c.z);
    local.push(0, 0);
    for (let i = 1; i <= RINGS; i++) {
      const k = i / RINGS;
      for (let s = 0; s < 6; s++) {
        const a = (Math.PI / 3) * s + Math.PI / 6;
        const lx = (Math.cos(a) * k) / 0.866, lz = (Math.sin(a) * k) / 0.866;
        pos.push(c.x + lx * size, c.y, c.z + lz * size);
        local.push(lx, -lz);
      }
    }
    for (let s = 0; s < 6; s++) index.push(base, base + 1 + ((s + 1) % 6), base + 1 + s);
    for (let i = 1; i < RINGS; i++) {
      const a0 = base + 1 + (i - 1) * 6, b0 = base + 1 + i * 6;
      for (let s = 0; s < 6; s++) {
        const s1 = (s + 1) % 6;
        index.push(a0 + s, a0 + s1, b0 + s, a0 + s1, b0 + s1, b0 + s);
      }
    }
  });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('aLocal', new THREE.Float32BufferAttribute(local, 2));
  const state = new THREE.Float32BufferAttribute(new Float32Array(centers.length * per * 4), 4).setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aState', state);
  geo.setIndex(index);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  mesh.renderOrder = 1;
  return { mesh, state, per };
};

const writeState = (st: THREE.BufferAttribute, i: number, per: number, x: number, y: number, z: number, w: number): void => {
  const a = st.array as Float32Array;
  for (let v = 0; v < per; v++) a.set([x, y, z, w], (i * per + v) * 4);
};

/* ---------- props ---------- */

/** An iron brazier: a tripod, a post and a wide bowl (one geometry). Its rim stands at 0.78. */
const brazierGeo = (): THREE.BufferGeometry => {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const leg = new THREE.CylinderGeometry(0.02, 0.026, 0.62, 5);
    leg.rotateZ(0.32);
    leg.rotateY(a);
    leg.translate(Math.cos(a) * 0.1, 0.3, -Math.sin(a) * 0.1);
    parts.push(leg.toNonIndexed());
  }
  const post = new THREE.CylinderGeometry(0.035, 0.045, 0.5, 6).translate(0, 0.42, 0);
  parts.push(post.toNonIndexed());
  const prof = [new THREE.Vector2(0.02, 0), new THREE.Vector2(0.12, 0.02), new THREE.Vector2(0.22, 0.1), new THREE.Vector2(0.26, 0.17), new THREE.Vector2(0.24, 0.17), new THREE.Vector2(0.2, 0.11), new THREE.Vector2(0.1, 0.05)];
  parts.push(new THREE.LatheGeometry(prof, 10).translate(0, 0.62, 0).toNonIndexed());
  const g = mergeGeometries(parts) as THREE.BufferGeometry;
  g.computeVertexNormals();
  return g;
};

const flameGeo = (): THREE.BufferGeometry => {
  const a = new THREE.ConeGeometry(0.16, 0.5, 7).translate(0, 0.25, 0);
  const b = new THREE.ConeGeometry(0.09, 0.34, 6).translate(0.05, 0.18, 0.03);
  const c = new THREE.ConeGeometry(0.08, 0.3, 6).translate(-0.06, 0.15, -0.02);
  return mergeGeometries([a, b, c]) as THREE.BufferGeometry;
};

/* ---------- each puzzle's view ---------- */

interface View {
  sync(p: DungeonPuzzle, tick: number, fresh: boolean): void;
  boom?(kind: string, x: number, y: number, tick: number): void;
  frame?(now: number): void;
  objects: THREE.Object3D[];
  /** Where each spot stands (for the solved burst). */
  spots: THREE.Vector3[];
}

export interface PuzzleFx {
  vfx: Vfx;
  glow: GlowLayer;
  flare: (p: THREE.Vector3, color: string, size: number, life: number) => void;
  low: boolean;
}

const UP = new THREE.Vector3(0, 1, 0);
const FIRE = new THREE.Color('#ff7a2a');
const PALE = new THREE.Color('#dfe4ff');
const RED = new THREE.Color('#ff4f3a');
const VIOLET = new THREE.Color('#b9a6ff');

class Braziers implements View {
  objects: THREE.Object3D[] = [];
  spots: THREE.Vector3[];
  private ring: { mesh: THREE.Mesh; state: THREE.BufferAttribute };
  private flames: THREE.InstancedMesh;
  private ash: THREE.InstancedMesh;
  private wasLit: boolean[] = [];
  private allLit = false;
  private flareT = 0;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private sc = new THREE.Vector3();

  constructor(
    p: DungeonPuzzle,
    ground: (x: number, z: number) => number,
    private u: { uTime: THREE.IUniform },
    private fx: PuzzleFx,
  ) {
    this.spots = p.spots.map((h) => at(h, ground));
    const n = this.spots.length;
    const stand = new THREE.InstancedMesh(brazierGeo(), new THREE.MeshStandardMaterial({ color: '#2a292e', metalness: 0.75, roughness: 0.45, flatShading: true }), n);
    this.ash = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(0.17, 0), new THREE.MeshStandardMaterial({ color: '#6a6662', roughness: 1, flatShading: true }), n);
    this.flames = new THREE.InstancedMesh(flameGeo(), new THREE.MeshStandardMaterial({ color: '#ffd59a', emissive: '#ff5a1a', emissiveIntensity: 2.2, transparent: true, opacity: 0.92, depthWrite: false, roughness: 0.5 }), n);
    this.flames.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.spots.forEach((s, i) => {
      this.m.compose(s, this.q.setFromAxisAngle(UP, i * 1.3), this.sc.set(1, 1, 1));
      stand.setMatrixAt(i, this.m);
      this.m.compose(this.v.copy(s).setY(s.y + 0.73), this.q, this.sc.set(1, 0.32, 1));
      this.ash.setMatrixAt(i, this.m);
    });
    this.ring = quads(this.spots.map((s) => s.clone().setY(s.y + 0.04)), (DEEP_CRYPT.braziers.range / 1000) * WORLD_PER_HEX / 0.88, decal(BRAZIER_FRAG, this.u));
    for (const o of [stand, this.ash, this.flames]) o.frustumCulled = false;
    this.objects = [this.ring.mesh, stand, this.ash, this.flames];
  }

  sync(p: DungeonPuzzle, tick: number, fresh: boolean): void {
    const burnMax = Math.max(1, DEEP_CRYPT.braziers.burnTicks);
    const lightT = Math.max(1, p.need || DEEP_CRYPT.braziers.lightTicks);
    let all = true;
    if (p.solved && !this.allLit) this.flareT = 18;
    this.spots.forEach((s, i) => {
      const left = (p.lit[i] ?? 0) - tick;
      const burn = p.solved ? 1 : clamp01(left / burnMax);
      if (burn <= 0) all = false;
      // Catching: how long someone has stood by this unlit bowl (`heat`, or `hold` for the one being lit).
      const heat = p.heat?.[i] ?? (p.on.includes(i) ? p.hold : 0);
      const hold = burn <= 0 ? clamp01(heat / lightT) : 0;
      writeState(this.ring.state, i, 4, burn, hold, this.flareT / 18, 0);
      // The flame: big while fresh, shrinking toward the end; a small one catching while it is lit.
      const k = burn > 0 ? 0.45 + 0.75 * burn : hold * 0.35;
      const fl = 1 + (((tick * 7 + i * 13) % 5) - 2) * 0.05;
      this.q.setFromAxisAngle(UP, tick * 0.7 + i);
      this.m.compose(this.v.copy(s).setY(s.y + 0.76), this.q, this.sc.set(k, k * fl * 1.1, k));
      this.flames.setMatrixAt(i, this.m);
      if (k > 0) {
        this.fx.glow.add(s.x, s.y + 0.95, s.z, FIRE, 0.9 + 1.4 * k, 0.25 + 0.35 * k);
        if (fresh && (tick + i * 3) % (this.fx.low ? 4 : 2) === 0) this.fx.vfx.burst(s.clone().setY(s.y + 0.95 + k * 0.3), { n: 1, color: (tick + i) % 2 ? '#ffb060' : '#ff6a2a', speed: 0.008, up: 0.04, size: 0.07, life: 10, gravity: -0.001, spread: 0.08 });
      } else if (fresh && (tick + i * 5) % 24 === 0) {
        // Cold ash: a wisp of grey now and then.
        this.fx.vfx.burst(s.clone().setY(s.y + 0.82), { n: 1, color: '#4a4846', speed: 0.002, up: 0.012, size: 0.25, life: 16, smoke: true });
      }
      if (hold > 0) this.fx.glow.add(s.x, s.y + 0.82, s.z, PALE, 0.7 + hold, 0.18 + 0.3 * hold);
      const was = this.wasLit[i] ?? false;
      if (fresh && burn > 0 && !was) {
        this.fx.vfx.ring(s.clone().setY(s.y + 0.05), 0.2, 1.1, '#ff8a3d', 7, 0.9);
        this.fx.vfx.burst(s.clone().setY(s.y + 0.85), { n: this.fx.low ? 6 : 14, color: '#ffb060', speed: 0.04, up: 0.09, size: 0.1, life: 10, gravity: 0.004 });
      }
      this.wasLit[i] = burn > 0;
    });
    this.flames.instanceMatrix.needsUpdate = true;
    this.ring.state.needsUpdate = true;
    if (fresh && all && !this.allLit) {
      // All four at once: they roar up together.
      for (const s of this.spots) {
        this.fx.vfx.shaft(s.clone().setY(s.y + 0.8), s.clone().setY(s.y + 3.2), '#ff7a2a', 8, 0.3);
        this.fx.flare(s.clone().setY(s.y + 1.1), '#ff8a3d', 2.4, 8);
      }
    }
    this.allLit = all;
    if (fresh && this.flareT > 0) this.flareT--;
  }
}

class Levers implements View {
  objects: THREE.Object3D[] = [];
  spots: THREE.Vector3[];
  private plates: { mesh: THREE.Mesh; state: THREE.BufferAttribute };
  private handles: THREE.InstancedMesh;
  private side: THREE.Vector3[] = [];
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  /** Big enough to read from the table camera over the crypt's walls. */
  private sc = new THREE.Vector3(1.6, 1.6, 1.6);
  private lastStep = -1;

  constructor(
    p: DungeonPuzzle,
    ground: (x: number, z: number) => number,
    room: THREE.Vector3,
    private u: { uTime: THREE.IUniform },
    private fx: PuzzleFx,
  ) {
    this.spots = p.spots.map((h) => at(h, ground));
    const n = this.spots.length;
    this.plates = quads(this.spots.map((s) => s.clone().setY(s.y + 0.035)), WORLD_PER_HEX * 0.5, decal(LEVER_FRAG, this.u));
    const ironM = new THREE.MeshStandardMaterial({ color: '#5a5862', metalness: 0.6, roughness: 0.45, flatShading: true });
    const post = new THREE.InstancedMesh(mergeGeometries([new THREE.BoxGeometry(0.16, 0.32, 0.16).translate(0, 0.16, 0), new THREE.BoxGeometry(0.22, 0.06, 0.22).translate(0, 0.03, 0)]) as THREE.BufferGeometry, ironM, n);
    const handleGeo = mergeGeometries([new THREE.CylinderGeometry(0.022, 0.022, 0.5, 6).translate(0, 0.25, 0), new THREE.SphereGeometry(0.055, 8, 6).translate(0, 0.5, 0)]) as THREE.BufferGeometry;
    this.handles = new THREE.InstancedMesh(handleGeo, new THREE.MeshStandardMaterial({ color: '#8a8794', metalness: 0.7, roughness: 0.35 }), n);
    this.handles.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.spots.forEach((s, i) => {
      // The lever stands on its plate's outer edge, inside the reach that counts (a hero beside it is on the plate).
      const out = s.clone().sub(room).setY(0);
      if (out.lengthSq() < 1e-4) out.set(1, 0, 0);
      out.normalize();
      const side = s.clone().addScaledVector(out, WORLD_PER_HEX * 0.3);
      this.side.push(side);
      this.m.compose(side, this.q.setFromAxisAngle(UP, Math.atan2(out.x, out.z)), this.sc);
      post.setMatrixAt(i, this.m);
    });
    for (const o of [post, this.handles]) o.frustumCulled = false;
    this.objects = [this.plates.mesh, post, this.handles];
  }

  sync(p: DungeonPuzzle, tick: number, fresh: boolean): void {
    const rounds = Math.floor(this.spots.length / 2);
    const step = p.solved ? rounds : p.step;
    const prog = clamp01(p.hold / Math.max(1, p.need));
    this.spots.forEach((s, i) => {
      const round = Math.floor(i / 2);
      const active = !p.solved && round === step ? 1 : 0;
      const on = p.on.includes(i) ? 1 : 0;
      const done = round < step ? 1 : 0;
      writeState(this.plates.state, i, 4, active, on * active, done, active ? prog : 0);
      // The lever: up and waiting, down while held or once its round is done.
      const down = on || done ? 1 : 0;
      const side = this.side[i] as THREE.Vector3;
      const out = s.clone().sub(side).setY(0).normalize();
      this.e.set(0, Math.atan2(out.x, out.z), 0, 'YXZ');
      this.e.x = down ? 0.95 : -0.55;
      this.q.setFromEuler(this.e);
      this.m.compose(side.clone().setY(side.y + 0.3 * this.sc.y), this.q, this.sc);
      this.handles.setMatrixAt(i, this.m);
      if (active) {
        this.fx.glow.add(s.x, s.y + 0.1, s.z, on ? PALE : RED, 1.2, on ? 0.35 : 0.14 + 0.08 * Math.sin(tick * 0.5));
        // A beacon on the lever's knob, so it shows over the wall.
        if (!on) this.fx.glow.add(side.x, side.y + 0.95, side.z, RED, 0.7, 0.3 + 0.15 * Math.sin(tick * 0.5));
      } else if (done) this.fx.glow.add(s.x, s.y + 0.1, s.z, RED, 0.8, 0.08);
      // The next round's pair, faintly: where to go once this one holds.
      else if (!p.solved && round === step + 1) this.fx.glow.add(s.x, s.y + 0.1, s.z, RED, 0.9, 0.05);
    });
    this.handles.instanceMatrix.needsUpdate = true;
    this.plates.state.needsUpdate = true;
    // The shared hold: a line of light between the round's two plates, filling in from both ends.
    if (!p.solved && step < rounds) {
      const a = this.spots[step * 2] as THREE.Vector3;
      const b = this.spots[step * 2 + 1] as THREE.Vector3;
      if (a && b) {
        const N = 16;
        const lit = Math.round(prog * (N / 2));
        for (let k = 1; k < N; k++) {
          const t = k / N;
          const fromEnd = Math.min(k, N - k);
          const on = fromEnd <= lit;
          const pt = a.clone().lerp(b, t);
          this.fx.glow.add(pt.x, pt.y + 0.06, pt.z, on ? PALE : RED, on ? 0.75 : 0.4, on ? 0.6 : 0.12);
        }
        if (fresh && prog > 0 && tick % 3 === 0) this.fx.vfx.beam(a.clone().setY(a.y + 0.08), b.clone().setY(b.y + 0.08), '#dfe4ff', 2, 0.02 + 0.03 * prog, 0.15);
      }
    }
    if (fresh && this.lastStep >= 0 && step > this.lastStep) {
      // A round held: both plates flash and the levers lock down.
      for (const i of [this.lastStep * 2, this.lastStep * 2 + 1]) {
        const s = this.spots[i];
        if (!s) continue;
        this.fx.vfx.ring(s.clone().setY(s.y + 0.05), 0.2, 1.2, '#dfe4ff', 8, 1);
        this.fx.flare(s.clone().setY(s.y + 0.4), '#dfe4ff', 1.6, 6);
      }
    }
    this.lastStep = step;
  }
}

class Runes implements View {
  objects: THREE.Object3D[] = [];
  spots: THREE.Vector3[];
  private seal: SealPlates;
  private flash: number[] = [];

  constructor(
    p: DungeonPuzzle,
    ground: (x: number, z: number) => number,
    private fx: PuzzleFx,
  ) {
    this.spots = p.spots.map((h) => at(h, ground, 0.035));
    this.seal = new SealPlates(this.spots, WORLD_PER_HEX * 0.42);
    this.objects = [this.seal.mesh];
  }

  sync(p: DungeonPuzzle, tick: number, fresh: boolean): void {
    const st = this.seal.sync({ plates: p.spots, order: p.lit, step: p.step, showFrom: p.showFrom, showUntil: p.showUntil, solved: p.solved }, tick);
    st.forEach((s, i) => {
      const a = this.spots[i] as THREE.Vector3;
      const g = Math.max(s.lit * 0.6, s.flash);
      if (g > 0) this.fx.glow.add(a.x, a.y + 0.1, a.z, RED, 1.1, 0.08 + 0.14 * g);
      if (fresh && s.flash > 0.9 && (this.flash[i] ?? 0) <= 0.9) {
        this.fx.vfx.ring(a.clone().setY(a.y + 0.02), 0.2, 1.1, '#ff8a6e', 9, 0.95);
        this.fx.vfx.burst(a.clone().setY(a.y + 0.15), { n: this.fx.low ? 5 : 10, color: '#ffb59e', speed: 0.02, up: 0.07, size: 0.1, life: 10, gravity: -0.001, spread: 0.25 });
      }
      this.flash[i] = s.flash;
    });
  }

  frame(now: number): void {
    this.seal.frame(now);
  }
}

class Bridge implements View {
  objects: THREE.Object3D[] = [];
  spots: THREE.Vector3[];
  private tiles: { mesh: THREE.Mesh; state: THREE.BufferAttribute; per: number };
  private cells: Hex[];
  private pathIx: number[];
  private broke = new Map<number, number>();
  private glowOn: boolean[] = [];

  constructor(
    p: DungeonPuzzle,
    L: DungeonLayout,
    ground: (x: number, z: number) => number,
    private u: { uTime: THREE.IUniform },
    private fx: PuzzleFx,
  ) {
    const room = L.rooms[p.room];
    this.cells = room ? L.floor.filter((h) => hexDist(h, room) <= room.radius) : [];
    // The path's tiles are part of the room even if they sit in a doorway.
    for (const h of p.spots) if (!this.cells.some((c) => c.q === h.q && c.r === h.r)) this.cells.push(h);
    this.pathIx = this.cells.map((c) => p.spots.findIndex((h) => h.q === c.q && h.r === c.r));
    this.spots = p.spots.map((h) => at(h, ground, 0.04));
    this.tiles = hexes(this.cells.map((h) => at(h, ground, 0.034)), WORLD_PER_HEX * 0.5, decal(BRIDGE_FRAG, this.u));
    this.objects = [this.tiles.mesh];
  }

  sync(p: DungeonPuzzle, tick: number, fresh: boolean): void {
    const n = Math.max(1, p.spots.length);
    const showing = tick < p.showUntil;
    const fadeK = clamp01((p.showUntil - tick) / 10);
    // A pulse runs down the path from the door in to the door out while it shows.
    const head = ((tick % 20) / 20) * (n + 4) - 2;
    this.cells.forEach((c, i) => {
      const k = this.pathIx[i] ?? -1;
      let path = 0;
      if (k >= 0 && showing) path = (0.62 + 0.38 * Math.exp(-((k - head) * (k - head)) / 2.5)) * (0.35 + 0.65 * fadeK);
      if (k >= 0 && p.solved) path = Math.max(path, 0.45);
      const got = k >= 0 && k <= p.step ? 1 : 0;
      const b = this.broke.get(i);
      const br = b === undefined ? 0 : clamp01(1 - (tick - b) / 36);
      if (b !== undefined && br <= 0) this.broke.delete(i);
      writeState(this.tiles.state, i, this.tiles.per, path, got, br, (c.q * 0.37 + c.r * 0.73) % 1);
      const was = this.glowOn[i] ?? false;
      if (path > 0.3) {
        const s = this.spots[k] as THREE.Vector3;
        this.fx.glow.add(s.x, s.y + 0.05, s.z, PALE, 1.1, 0.18 * path);
        if (fresh && !was && showing) this.fx.vfx.burst(s.clone().setY(s.y + 0.05), { n: 2, color: '#eef0ff', speed: 0.004, up: 0.03, size: 0.08, life: 10, gravity: -0.001, spread: 0.3 });
      } else if (got && k >= 0) {
        const s = this.spots[k] as THREE.Vector3;
        this.fx.glow.add(s.x, s.y + 0.05, s.z, VIOLET, 0.9, 0.06);
      }
      this.glowOn[i] = path > 0.3;
    });
    this.tiles.state.needsUpdate = true;
  }

  /** A hero fell through: the tile under that spot shatters for a moment. */
  boom(kind: string, x: number, y: number, tick: number): void {
    if (kind !== 'glassFall') return;
    let best = -1;
    let bd = Infinity;
    this.cells.forEach((c, i) => {
      const w = hexToWorld(c.q, c.r);
      const d = (w.x - x) * (w.x - x) + (w.y - y) * (w.y - y);
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    if (best >= 0) this.broke.set(best, tick);
  }
}

const sameSpots = (a: readonly Hex[], b: readonly Hex[]): boolean => a.length === b.length && a.every((h, i) => h.q === b[i]?.q && h.r === b[i]?.r);

/** Every puzzle of the deep crypt, built the first time the state shows it (and again if it is drawn anew). */
export class PuzzleLayer {
  readonly group = new THREE.Group();
  private views = new Map<number, { view: View; kind: string; spots: Hex[] }>();
  private u = { uTime: { value: 0 } };

  constructor(
    private layout: DungeonLayout,
    private ground: (x: number, z: number) => number,
    private fx: PuzzleFx,
  ) {
    this.group.name = 'puzzles';
  }

  sync(puzzles: readonly DungeonPuzzle[] | undefined, tick: number, fresh: boolean): void {
    if (!puzzles) return;
    for (const p of puzzles) {
      let v = this.views.get(p.room);
      // A party room rebuilds the crypt while it gathers: a bigger party gets more braziers, elsewhere.
      if (!v || v.kind !== p.kind || !sameSpots(v.spots, p.spots)) {
        if (v) for (const o of v.view.objects) this.group.remove(o);
        const room = this.layout.rooms[p.room];
        const c = room ? hexToWorld(room.q, room.r) : { x: 0, y: 0 };
        const center = new THREE.Vector3(c.x * K, 0, c.y * K);
        const view: View =
          p.kind === 'braziers' ? new Braziers(p, this.ground, this.u, this.fx) : p.kind === 'levers' ? new Levers(p, this.ground, center, this.u, this.fx) : p.kind === 'runes' ? new Runes(p, this.ground, this.fx) : new Bridge(p, this.layout, this.ground, this.u, this.fx);
        v = { view, kind: p.kind, spots: p.spots.map((h) => ({ q: h.q, r: h.r })) };
        this.views.set(p.room, v);
        for (const o of view.objects) this.group.add(o);
      }
      v.view.sync(p, tick, fresh);
    }
  }

  /**
   * A puzzle's burst (`ev boom`): a brazier catching, a round of the levers held, a hero falling through
   * the glass, a puzzle solved. Returns whether it was one of these.
   */
  boom(kind: string, x: number, y: number, tick: number): boolean {
    const p = new THREE.Vector3(x * K, this.ground(x * K, y * K) + 0.05, y * K);
    const { vfx, low } = this.fx;
    if (kind === 'brazier') {
      vfx.ring(p, 0.2, 1.3, '#ff8a3d', 8, 1);
      vfx.burst(p.clone().setY(p.y + 0.85), { n: low ? 8 : 18, color: '#ffb060', speed: 0.05, up: 0.1, size: 0.1, life: 11, gravity: 0.004 });
      this.fx.flare(p.clone().setY(p.y + 0.9), '#ff8a3d', 1.8, 6);
      return true;
    }
    if (kind === 'brazierOut') {
      // A bowl burnt out before the others caught: a puff of grey and a last few sparks.
      const q = p.clone().setY(p.y + 0.8);
      vfx.burst(q, { n: low ? 3 : 6, color: '#4a4846', speed: 0.01, up: 0.025, size: 0.4, life: 18, smoke: true });
      vfx.burst(q, { n: low ? 3 : 6, color: '#ff6a2a', speed: 0.02, up: 0.04, size: 0.06, life: 8, gravity: 0.006 });
      return true;
    }
    if (kind === 'lever') {
      vfx.ring(p, 0.2, 1.5, '#dfe4ff', 9, 1);
      vfx.shaft(p.clone().setY(p.y + 0.02), p.clone().setY(p.y + 1.6), '#dfe4ff', 6, 0.2);
      vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 6 : 12, color: '#ffffff', speed: 0.04, up: 0.06, size: 0.08, life: 9, gravity: 0.004 });
      this.fx.flare(p.clone().setY(p.y + 0.4), '#dfe4ff', 1.6, 5);
      return true;
    }
    if (kind === 'glassFall') {
      for (const { view } of this.views.values()) view.boom?.(kind, x, y, tick);
      vfx.ring(p, 0.15, 1.0, '#eef0ff', 6, 0.9);
      vfx.burst(p.clone().setY(p.y + 0.15), { n: low ? 10 : 22, color: '#e6e0ff', speed: 0.05, up: 0.05, size: 0.07, life: 12, gravity: 0.012, spread: 0.3 });
      vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 4 : 8, color: '#8a5cff', speed: 0.03, up: 0.02, size: 0.09, life: 10, gravity: 0.016 });
      this.fx.flare(p.clone().setY(p.y + 0.2), '#d9ccff', 1.4, 4);
      return true;
    }
    if (kind === 'puzzleSolved') {
      // The room gives in: a great ring, a column of light, every piece of the puzzle answering.
      vfx.ring(p, 0.4, 5.5, '#eef0ff', 16, 1);
      vfx.ring(p, 0.3, 3.2, '#ff8a6e', 12, 0.85);
      vfx.shaft(p.clone().setY(p.y + 0.02), p.clone().setY(p.y + 4.5), '#eef0ff', 12, 0.55);
      vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 16 : 40, color: '#ffffff', speed: 0.07, up: 0.12, size: 0.1, life: 16, gravity: 0.003, spread: 0.6 });
      this.fx.flare(p.clone().setY(p.y + 1), '#ffffff', 4.5, 10);
      this.fx.flare(p.clone().setY(p.y + 0.4), '#ff8a6e', 3, 8);
      let best: View | null = null;
      let bd = Infinity;
      for (const { view } of this.views.values()) {
        for (const s of view.spots) {
          const d = s.distanceToSquared(p);
          if (d < bd) {
            bd = d;
            best = view;
          }
        }
      }
      if (best && bd < 400) for (const s of best.spots) vfx.beam(s.clone().setY(s.y + 0.15), p.clone().setY(p.y + 1.2), '#eef0ff', 8, 0.035, 0.6);
      return true;
    }
    return false;
  }

  frame(now: number): void {
    this.u.uTime.value = now % 3600;
    for (const { view } of this.views.values()) view.frame?.(now);
  }

  dispose(): void {
    this.group.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
    });
  }
}
